import React, { useEffect, useState } from "react";
import { auth, db, storage } from "./firebase";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { THEMES } from "./themes";
import { applyTheme } from "./themeUtils";
import logoUrl from "./assets/logo1.png";

const DEFAULT_CUSTOM_THEME = {
  bg: "#f4f3ee",
  panel: "#fffdf9",
  "upload-panel": "#7e8c73",
  "browse-button": "#c4c7a9",
  "upload-button": "#b3b89d",
  "browse-button-end": "#899174",
  "upload-button-end": "#7a8169",
  "button-gradient-angle": "180deg",
  "panel-2": "#ece8df",
  text: "#2d3328",
  muted: "#6b715d",
  stroke: "rgba(0,0,0,.08)",
  gold: "#7e8c73",
  gold2: "#68745f",
  gold3: "#a9b39a",
  danger: "#b56953",
  "danger-2": "#955747"
};

const THEME_EDITOR_FIELDS = [
  { key: "upload-panel", label: "Upload Panel" },
  { key: "browse-button", label: "Browse Files Button" },
  { key: "upload-button", label: "Upload Content Button" },
];

export default function SuperAdmin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [themePreview, setThemePreview] = useState(DEFAULT_CUSTOM_THEME);

  // apply ivory gold theme when component mounts
  useEffect(() => {
    applyTheme("ivoryGold");
  }, []);

  // Form state for new/edit event
  const [eventForm, setEventForm] = useState({
    weddingId: "",
    coupleName: "",
    eventDate: "",
    eventType: "Wedding",
    theme: "classic",
    customTheme: null,
    uploadPanelColor: DEFAULT_CUSTOM_THEME["upload-panel"],
    browseButtonColor: DEFAULT_CUSTOM_THEME["browse-button"],
    uploadButtonColor: DEFAULT_CUSTOM_THEME["upload-button"],
    backgroundType: "color", // "color", "image", "video"
    backgroundFile: null,
    backgroundFileList: [],
    backgroundImages: [],
    backgroundUrl: "",
    admins: [],
    showGuestNavbar: true
  });

  // Check if user is a super admin by looking up their email in Firestore
  const checkSuperAdminPermissions = async (email) => {
    try {
      const configDoc = await getDoc(doc(db, "config", "superAdmins"));
      
      if (configDoc.exists()) {
        const data = configDoc.data();
        
        // Normalize email to lowercase for comparison
        const normalizedEmail = email?.toLowerCase().trim();
        const normalizedEmails = data.emails?.map(e => e.toLowerCase().trim()) || [];
        
        const isSuperAdmin = normalizedEmails.includes(normalizedEmail);
        return isSuperAdmin;
      } else {
        console.error("❌ config/superAdmins document does not exist");
        return false;
      }
    } catch (err) {
      console.error("❌ Error checking super admin permissions:", err);
      alert(`Debug: ${err.message}`);
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      
      if (!isMounted) {
        // component unmounted, ignore auth change
        return;
      }
      
      setUser(u);
      setCheckingAuth(false);
    });
    
    return () => {
      // cleaning up auth listener
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Separate effect to check super admin permissions
  useEffect(() => {
    if (user && checkingAuth === false) {
      checkSuperAdminPermissions(user.email).then((isSuperAdmin) => {
        if (!isSuperAdmin) {
          alert("Not authorized as super admin.");
          signOut(auth);
        } else {
          // super admin access confirmed
        }
      });
    }
  }, [user, checkingAuth]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const eventsRef = collection(db, "events");

      const q = query(eventsRef, orderBy("eventDate", "desc"));

      const snap = await getDocs(q);


      const eventsData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setEvents(eventsData);
    } catch (err) {
      console.error("❌ Error loading events:", err);
      console.error("❌ Error details:", err.code, err.message);
      alert("Error loading events: " + err.message);
      // Do NOT sign out the user! Just show the error
    }
    setLoading(false);
  };

  const fetchUpcomingEvents = async () => {
    try {
      const eventsRef = collection(db, "events");
      // Only show future events or events from the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const q = query(
        eventsRef,
        orderBy("eventDate", "asc")
      );
      const snap = await getDocs(q);

      const eventsData = snap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(event => {
          const eventDate = event.eventDate?.toDate?.() || new Date(event.eventDate);
          return eventDate >= thirtyDaysAgo;
        });

      setUpcomingEvents(eventsData);
      setUpcomingLoading(false);
    } catch (err) {
      console.error("Error fetching upcoming events:", err);
      setUpcomingLoading(false);
    }
  };

  // Separate effect to fetch events when user is set
  useEffect(() => {
    if (user && checkingAuth === false) {
      fetchEvents();
      fetchUpcomingEvents();
    }
  }, [user, checkingAuth]);

  const handleLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      alert("Login failed. Please check your credentials.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const resetForm = () => {
    setEventForm({
      weddingId: "",
      coupleName: "",
      eventDate: "",
      eventType: "Wedding",
      theme: "classic",
      customTheme: null,
      uploadPanelColor: DEFAULT_CUSTOM_THEME["upload-panel"],
      browseButtonColor: DEFAULT_CUSTOM_THEME["browse-button"],
      uploadButtonColor: DEFAULT_CUSTOM_THEME["upload-button"],
      backgroundType: "color",
      backgroundFile: null,
      backgroundFileList: [],
      backgroundImages: [],
      backgroundUrl: "",
      admins: [],
      showGuestNavbar: true
    });
    setEditingEvent(null);
    setShowAddForm(false);
    setShowThemeModal(false);
  };

  const openThemeEditor = () => {
    const baseTheme = THEMES[eventForm.theme]?.colors || THEMES.classic.colors;
    setThemePreview({
      ...baseTheme,
      "upload-panel": eventForm.uploadPanelColor || DEFAULT_CUSTOM_THEME["upload-panel"],
      "browse-button": eventForm.browseButtonColor || DEFAULT_CUSTOM_THEME["browse-button"],
      "upload-button": eventForm.uploadButtonColor || DEFAULT_CUSTOM_THEME["upload-button"]
    });
    setShowThemeModal(true);
  };

  const resetThemePreview = () => {
    const baseTheme = THEMES[eventForm.theme]?.colors || THEMES.classic.colors;
    setThemePreview({
      ...baseTheme,
      "upload-panel": DEFAULT_CUSTOM_THEME["upload-panel"],
      "browse-button": DEFAULT_CUSTOM_THEME["browse-button"],
      "upload-button": DEFAULT_CUSTOM_THEME["upload-button"]
    });
    setEventForm(prev => ({
      ...prev,
      customTheme: null,
      uploadPanelColor: DEFAULT_CUSTOM_THEME["upload-panel"],
      browseButtonColor: DEFAULT_CUSTOM_THEME["browse-button"],
      uploadButtonColor: DEFAULT_CUSTOM_THEME["upload-button"]
    }));
  };

  const updateThemePreview = (key, value) => {
    setThemePreview(prev => ({ ...prev, [key]: value }));
  };

  // convert HEIC to JPEG using canvas if browser supports it
  const convertHeicToJpeg = async (file) => {
    return new Promise((resolve, reject) => {

      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          if (!canvas) {
            throw new Error('Failed to create canvas element');
          }

          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Failed to get 2D context from canvas');
          }

          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            blob => {
              if (blob) {
                const newFile = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
                resolve(newFile);
              } else {
                console.error('❌ toBlob returned null blob');
                reject(new Error('Canvas toBlob returned null - conversion failed'));
              }
            },
            'image/jpeg',
            0.92
          );
        } catch (err) {
          console.error('❌ Canvas operation failed:', err.message);
          reject(new Error(`Canvas conversion error: ${err.message}`));
        }
      };
      img.onerror = (e) => {
        console.error('❌ Image failed to load:', e, 'src was:', img.src);
        reject(new Error('Failed to load HEIC image - browser may not support HEIC format or file is corrupted'));
      };

      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    });
  };

  const normalizeBackgroundImageList = (background) => {
    if (!background) return [];

    if (Array.isArray(background.images) && background.images.length > 0) {
      return background.images.filter(item => item && item.url).map(item => ({
        id: item.id || `${item.filename || item.url}-${Math.random().toString(16).slice(2)}`,
        filename: item.filename || "",
        url: item.url,
        file: null
      }));
    }

    if (background.url) {
      return [{
        id: `${background.filename || background.url}-single`,
        filename: background.filename || "",
        url: background.url,
        file: null
      }];
    }

    return [];
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const normalized = [];
    for (const file of selectedFiles) {
      let nextFile = file;
      const lower = file.name.toLowerCase();
      if ((lower.endsWith('.heic') || file.type === 'image/heic')) {
        try {
          nextFile = await convertHeicToJpeg(file);
        } catch (err) {
          console.warn('❌ Background HEIC conversion failed', err.message);
          alert(`Background HEIC conversion failed: ${err.message}. The file will be uploaded as HEIC and may not display properly. Please upload a JPG/PNG if possible.`);
        }
      }

      normalized.push({
        id: `${nextFile.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file: nextFile,
        filename: nextFile.name,
        url: URL.createObjectURL(nextFile)
      });
    }

    setEventForm(prev => {
      const existing = prev.backgroundImages || [];
      const merged = [...existing, ...normalized];
      const primary = merged[0]?.url || prev.backgroundUrl || "";

      return {
        ...prev,
        backgroundFileList: merged.map(item => item.file).filter(Boolean),
        backgroundImages: merged,
        backgroundUrl: primary,
        backgroundFile: merged[0]?.file || null
      };
    });
  };

  const removeBackgroundImage = (indexToRemove) => {
    setEventForm(prev => {
      const nextImages = (prev.backgroundImages || []).filter((_, index) => index !== indexToRemove);
      const nextPrimary = nextImages[0]?.url || "";

      return {
        ...prev,
        backgroundImages: nextImages,
        backgroundFileList: nextImages.map(item => item.file).filter(Boolean),
        backgroundUrl: nextPrimary,
        backgroundFile: nextImages[0]?.file || null
      };
    });
  };

  const reorderBackgroundImage = (fromIndex, toIndex) => {
    setEventForm(prev => {
      const current = [...(prev.backgroundImages || [])];
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= current.length || toIndex >= current.length) {
        return prev;
      }

      const [moved] = current.splice(fromIndex, 1);
      current.splice(toIndex, 0, moved);
      const nextPrimary = current[0]?.url || "";

      return {
        ...prev,
        backgroundImages: current,
        backgroundFileList: current.map(item => item.file).filter(Boolean),
        backgroundUrl: nextPrimary,
        backgroundFile: current[0]?.file || null
      };
    });
  };

  const downloadBackgroundFile = async (background, fallbackName) => {
    if (!background?.url) return;

    const filename = background.filename || fallbackName;

    try {
      const response = await fetch(background.url);
      if (!response.ok) throw new Error(`Download failed with status ${response.status}`);

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.warn("Could not download background as a file, opening the source instead:", err);
      const link = document.createElement("a");
      link.href = background.url;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const uploadBackgroundFile = async (file, weddingId) => {
    // ensure we have a file to upload
    if (!file) return null;

    // if the file still appears to be HEIC, try converting again here as a last resort
    const lowerName = file.name.toLowerCase();
    if ((lowerName.endsWith('.heic') || file.type === 'image/heic')) {
      try {
        const converted = await convertHeicToJpeg(file);
        file = converted;
      } catch (err) {
        console.warn('❌ uploadBackgroundFile conversion failed, continuing with original', err.message);
        alert(`Background HEIC conversion failed during upload: ${err.message}. The file will be stored as HEIC and may not display correctly.`);
      }
    }

    const timestamp = Date.now();
    const ext = file.name.split('.').pop();
    const filename = `backgrounds/${weddingId}/background-${timestamp}.${ext}`;
    const storageRef = ref(storage, filename);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    return { filename, url };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let backgroundData = { type: eventForm.backgroundType };

      // If we're not just using a theme color, prepare to upload a file.  This can
      // come from the form state (new selection) or from the existing event when
      // editing a HEIC background that hasn't been converted yet.
      let fileToUpload = null;
      if (eventForm.backgroundType !== "color") {
        if (eventForm.backgroundFile) {
          fileToUpload = eventForm.backgroundFile;
        } else if (
          editingEvent &&
          editingEvent.background?.url &&
          editingEvent.background.type === 'image' &&
          editingEvent.background.url.toLowerCase().endsWith('.heic')
        ) {
          // user didn't pick a new file, but the existing one is HEIC; try to fetch
          // it and convert before re-uploading so the stored event is not stuck with HEIC
          try {
            const resp = await fetch(editingEvent.background.url);
            const blob = await resp.blob();
            const heicFile = new File([blob], 'background.heic', { type: blob.type || 'image/heic' });
            fileToUpload = await convertHeicToJpeg(heicFile);
          } catch (err) {
            console.warn('❌ could not convert existing background HEIC:', err.message);
          }
        }

        if (fileToUpload) {
          const uploadResult = await uploadBackgroundFile(fileToUpload, eventForm.weddingId);
          if (uploadResult) {
            backgroundData.filename = uploadResult.filename;
            backgroundData.url = uploadResult.url;
          }
        } else if (editingEvent && editingEvent.background?.url) {
          // No new file uploaded, preserve existing background data
          backgroundData.filename = editingEvent.background.filename;
          backgroundData.url = editingEvent.background.url;
        }
      }

      if (eventForm.backgroundType === "image") {
        const selectedImages = [];

        for (const item of eventForm.backgroundImages || []) {
          if (!item) continue;

          if (item.file) {
            const uploadResult = await uploadBackgroundFile(item.file, eventForm.weddingId);
            if (uploadResult) {
              selectedImages.push({ filename: uploadResult.filename, url: uploadResult.url });
            }
          } else if (item.url) {
            selectedImages.push({
              filename: item.filename || item.url.split('/').pop() || 'background-image',
              url: item.url
            });
          }
        }

        if (selectedImages.length > 0) {
          backgroundData.images = selectedImages;
          backgroundData.filename = selectedImages[0].filename;
          backgroundData.url = selectedImages[0].url;
        } else {
          backgroundData.images = [];
          backgroundData.filename = "";
          backgroundData.url = "";
        }
      }

      const eventData = {
        weddingId: eventForm.weddingId,
        coupleName: eventForm.coupleName,
        eventDate: new Date(eventForm.eventDate),
        eventType: eventForm.eventType,
        theme: eventForm.theme === "custom" ? "vintageWedding" : eventForm.theme,
        customTheme: null,
        uploadPanelColor: eventForm.uploadPanelColor || DEFAULT_CUSTOM_THEME["upload-panel"],
        browseButtonColor: eventForm.browseButtonColor || DEFAULT_CUSTOM_THEME["browse-button"],
        uploadButtonColor: eventForm.uploadButtonColor || DEFAULT_CUSTOM_THEME["upload-button"],
        background: backgroundData,
        admins: eventForm.admins,
        showGuestNavbar: Boolean(eventForm.showGuestNavbar),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingEvent) {
        // Update existing event
        await updateDoc(doc(db, "events", editingEvent.id), {
          ...eventData,
          updatedAt: new Date()
        });
      } else {
        // Create new event using weddingId as the document ID
        if (!eventForm.weddingId || eventForm.weddingId.trim() === "") {
          alert("Please enter a Wedding ID");
          return;
        }
        await setDoc(doc(db, "events", eventForm.weddingId), eventData);
      }

      resetForm();
      await fetchEvents();
      alert(editingEvent ? "Event updated successfully!" : "Event created successfully!");
    } catch (err) {
      console.error("Error saving event:", err);
      alert("Error saving event. Please try again.");
    }
  };

  const handleEdit = (event) => {
    const backgroundImages = normalizeBackgroundImageList(event.background || null);
    const selectedTheme = event.theme === "custom"
      ? "vintageWedding"
      : (THEMES[event.theme] ? event.theme : "classic");
    setEventForm({
      weddingId: event.weddingId,
      coupleName: event.coupleName,
      eventDate: event.eventDate?.toDate?.()?.toISOString().split('T')[0] || "",
      eventType: event.eventType || "Wedding",
      theme: selectedTheme,
      customTheme: null,
      uploadPanelColor: event.uploadPanelColor || event.customTheme?.["upload-panel"] || DEFAULT_CUSTOM_THEME["upload-panel"],
      browseButtonColor: event.browseButtonColor || DEFAULT_CUSTOM_THEME["browse-button"],
      uploadButtonColor: event.uploadButtonColor || DEFAULT_CUSTOM_THEME["upload-button"],
      backgroundType: event.background?.type || "color",
      backgroundFile: null,
      backgroundFileList: [],
      backgroundImages,
      backgroundUrl: backgroundImages[0]?.url || event.background?.url || "",
      admins: event.admins || [],
      showGuestNavbar: event.showGuestNavbar !== false
    });
    setEditingEvent(event);
    setShowAddForm(true);
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Are you sure you want to delete the event "${event.coupleName}"?`)) return;

    try {
      // Delete background file if exists — log but don't abort if Storage rules block it
      if (event.background?.filename) {
        try {
          const backgroundRef = ref(storage, event.background.filename);
          await deleteObject(backgroundRef);
        } catch (storageErr) {
          console.warn("Could not delete background file from Storage (check storage rules):", storageErr.message);
        }
      }

      // Delete event document
      await deleteDoc(doc(db, "events", event.id));

      await fetchEvents();
      alert("Event deleted successfully!");
    } catch (err) {
      console.error("Error deleting event:", err);
      alert("Error deleting event. Please try again.");
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  };

  const getThemeLabel = (event) => {
    if (event?.customTheme) return "Custom Theme 1";
    return THEMES[event?.theme]?.name || event?.theme || "Classic";
  };

  if (checkingAuth) return <p className="p-4">Checking authentication...</p>;

  if (!user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "radial-gradient(circle at top, rgba(168,148,94,0.18), transparent 35%), #171918",
          padding: "24px"
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "440px",
            background: "rgba(25, 27, 28, 0.88)",
            border: "1px solid rgba(214, 193, 140, 0.25)",
            borderRadius: "22px",
            boxShadow: "0 28px 80px rgba(0,0,0,0.45)",
            overflow: "hidden",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)"
          }}
        >
          <div
            style={{
              padding: "24px 24px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              borderBottom: "1px solid rgba(255,255,255,0.08)"
            }}
          >
            <img
              src={logoUrl}
              alt="EventSnap"
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                objectFit: "cover",
                boxShadow: "0 0 0 1px rgba(255,255,255,0.15)"
              }}
            />
            <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: 0.2, color: "#f4efe9", lineHeight: 1 }}>
              Event<span style={{ color: "#d9c48d" }}>Snap</span>
            </div>
          </div>

          <div style={{ padding: "28px 26px 24px" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 12, letterSpacing: 2.4, textTransform: "uppercase", color: "#d9c48d", marginBottom: 8 }}>
                Super Admin
              </div>
              <h2 style={{ margin: 0, fontSize: 30, fontWeight: 700, color: "#f3efe9" }}>Welcome back</h2>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <label style={{ display: "block", marginBottom: 8, fontSize: 13, color: "#d9d0bf", fontWeight: 600 }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@eventsnap.com"
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.04)",
                    color: "#f5f0e8",
                    padding: "12px 14px",
                    outline: "none"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, fontSize: 13, color: "#d9d0bf", fontWeight: 600 }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.04)",
                    color: "#f5f0e8",
                    padding: "12px 14px",
                    outline: "none"
                  }}
                />
              </div>

              <button
                onClick={handleLogin}
                style={{
                  marginTop: 8,
                  width: "100%",
                  border: "none",
                  borderRadius: 12,
                  padding: "14px 16px",
                  background: "linear-gradient(135deg, #d7c38d, #bca66b)",
                  color: "#1a1b1d",
                  fontWeight: 800,
                  fontSize: 16,
                  boxShadow: "0 10px 24px rgba(189, 164, 96, 0.35)"
                }}
              >
                Login to dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard">
      <div className="super-admin-shell">
        <div className="super-admin-header">
          <div>
            <div className="super-admin-kicker">Super Admin</div>
            <h1 className="super-admin-title">Dashboard</h1>
          </div>
          <div className="super-admin-actions">
            <button onClick={() => setShowAddForm(true)} className="super-admin-btn super-admin-btn-primary me-2">
              Add New Event
            </button>
            <button onClick={handleLogout} className="super-admin-btn super-admin-btn-ghost">
              Logout
            </button>
          </div>
        </div>

      {showThemeModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1050,
            background: "#f4f3f1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            opacity: 1
          }}
          onClick={() => setShowThemeModal(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "1350px",
              maxHeight: "calc(100vh - 32px)",
              background: "#ffffff",
              border: "1px solid rgba(0,0,0,.08)",
              borderRadius: "16px",
              boxShadow: "0 30px 80px rgba(0,0,0,.18)",
              overflow: "hidden",
              opacity: 1,
              display: "flex",
              flexDirection: "column"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#ffffff",
                borderBottom: "1px solid rgba(0,0,0,.08)",
                padding: "1rem 1.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <h5 style={{ margin: 0, fontSize: "1.25rem" }}>Customize guest theme</h5>
              <button type="button" className="btn-close" onClick={() => setShowThemeModal(false)} aria-label="Close" />
            </div>

            <div style={{ background: "#ffffff", padding: "0.5rem 0.75rem 0.75rem", opacity: 1, overflowY: "auto", flex: 1 }}>
              <div className="row g-2 align-items-center" style={{ margin: 0 }}>
                <div className="col-lg-8 d-flex justify-content-center" style={{ padding: "0.25rem 0.5rem 0.25rem 0.25rem" }}>
                  <div
                    className="position-relative overflow-hidden border"
                    style={{
                      background: themePreview.bg,
                      color: themePreview.text,
                      width: "100%",
                      maxWidth: "390px",
                      minHeight: "760px",
                      maxHeight: "calc(100vh - 120px)",
                      borderColor: themePreview.stroke || "rgba(0,0,0,.08)",
                      boxShadow: "0 18px 45px rgba(0,0,0,.12)",
                      opacity: 1,
                      filter: "none",
                      backgroundColor: themePreview.bg,
                      backgroundImage: "none",
                      borderRadius: "32px",
                      border: "8px solid #1b1d1f"
                    }}
                  >
                    {(eventForm.backgroundType === "image" || eventForm.backgroundType === "video") && eventForm.backgroundUrl ? (
                      eventForm.backgroundType === "video" ? (
                        <video
                          src={eventForm.backgroundUrl}
                          autoPlay
                          muted
                          loop
                          playsInline
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            filter: "brightness(0.52)"
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: `url(${eventForm.backgroundUrl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "brightness(0.55)"
                          }}
                        />
                      )
                    ) : null}

                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0)",
                        pointerEvents: "none"
                      }}
                    />

                    <div className="position-relative" style={{ zIndex: 2 }}>
                      <header
                        style={{
                          background: "#222326",
                          borderBottom: "1px solid rgba(255,255,255,0.08)",
                          padding: "10px 16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={logoUrl}
                            alt="EventSnap"
                            style={{
                              height: 32,
                              width: 32,
                              borderRadius: 10,
                              display: "block",
                              objectFit: "cover",
                              boxShadow: "0 0 0 1px rgba(255,255,255,0.12)"
                            }}
                          />
                          <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: 0.3, color: "#f7f3ee", lineHeight: 1 }}>
                            Event<span style={{ color: "#d7c38d" }}>Snap</span>
                          </div>
                        </div>
                      </header>

                      <div className="px-3 py-3">
                        <div className="mx-auto" style={{ maxWidth: 820 }}>
                          <div className="text-center mb-3">
                            <h1
                              style={{
                                margin: 0,
                                fontFamily: "'Dancing Script', cursive",
                                fontSize: "2.6rem",
                                fontWeight: 800,
                                color: themePreview.gold,
                                textShadow: "0 2px 10px rgba(0,0,0,0.18)"
                              }}
                            >
                              {eventForm.coupleName || "Sarah & Daniel"}
                            </h1>
                            <p
                              style={{
                                margin: "10px 0 0",
                                color: themePreview.muted,
                                fontSize: "1.05rem",
                                fontStyle: "italic",
                                textShadow: "0 2px 12px rgba(0,0,0,0.2)"
                              }}
                            >
                              {(eventForm.eventType || "Wedding") + " - " +
                                (eventForm.eventDate ? new Date(eventForm.eventDate).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric"
                                }) : "12 September 2026")}
                            </p>
                          </div>

                          <div className="mx-auto mt-3" style={{ width: "82%", maxWidth: 320 }}>
                            <div
                              className="rounded-4"
                              style={{
                                background: eventForm.backgroundType === "video" ? "rgba(12, 12, 12, 0.58)" : `color-mix(in srgb, ${themePreview["upload-panel"] || DEFAULT_CUSTOM_THEME["upload-panel"]} 48%, transparent)`,
                                border: `1px solid ${eventForm.backgroundType === "video" ? "rgba(255,255,255,0.18)" : (themePreview.stroke || "rgba(255,255,255,.18)")}`,
                                boxShadow: "0 14px 28px rgba(0,0,0,.12)",
                                opacity: 0.9,
                                backdropFilter: "blur(3px)",
                                WebkitBackdropFilter: "blur(3px)",
                                padding: "0.9rem 0.8rem"
                              }}
                            >
                              <div style={{ marginBottom: "0.5rem" }}>
                                <h2 className="text-center mb-1" style={{ fontWeight: 700, color: eventForm.backgroundType === "video" ? "#f5f0e8" : themePreview.text }}>
                                  Upload Your Photos/Videos
                                </h2>
                                <p className="text-center mb-3" style={{ color: eventForm.backgroundType === "video" ? "rgba(245,240,232,0.8)" : themePreview.muted, margin: 0 }}>
                                  Browse to upload (max 10)
                                </p>
                              </div>

                              <div
                                className="mb-3"
                                style={{
                                  border: eventForm.backgroundType === "video" ? "2px dashed rgba(255,255,255,0.26)" : "2px dashed rgba(126,140,115,0.8)",
                                  background: eventForm.backgroundType === "video" ? "rgba(255,255,255,0.08)" : `color-mix(in srgb, ${themePreview["upload-panel"] || DEFAULT_CUSTOM_THEME["upload-panel"]} 22%, transparent)`,
                                  borderRadius: 18,
                                  padding: 18,
                                  textAlign: "center",
                                  color: eventForm.backgroundType === "video" ? "#f5f0e8" : themePreview.text,
                                  opacity: 1,
                                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)"
                                }}
                              >
                                <div style={{ fontWeight: 600 }}>Drop your content here</div>
                                <small style={{ color: eventForm.backgroundType === "video" ? "rgba(245,240,232,0.8)" : themePreview.muted }}>or click to browse files</small>
                                <div className="mt-3">
                                  <span
                                    style={{
                                      display: "inline-block",
                                      background: `linear-gradient(${themePreview["button-gradient-angle"] || "180deg"}, ${themePreview["browse-button"] || DEFAULT_CUSTOM_THEME["browse-button"]}, ${themePreview["browse-button-end"] || DEFAULT_CUSTOM_THEME["browse-button-end"]})`,
                                      color: themePreview["button-text"] || themePreview.panel,
                                      borderRadius: 12,
                                      padding: "10px 20px",
                                      fontWeight: 700,
                                      border: "1px solid rgba(0,0,0,0.16)"
                                    }}
                                  >
                                    Browse Files
                                  </span>
                                </div>
                              </div>

                              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                <button
                                  type="button"
                                  className="py-2"
                                  style={{
                                    width: "100%",
                                    background: `linear-gradient(${themePreview["button-gradient-angle"] || "180deg"}, ${themePreview["upload-button"] || DEFAULT_CUSTOM_THEME["upload-button"]}, ${themePreview["upload-button-end"] || DEFAULT_CUSTOM_THEME["upload-button-end"]})`,
                                    color: themePreview["button-text"] || themePreview.panel,
                                    border: "none",
                                    borderRadius: 12,
                                    fontWeight: 700,
                                    minHeight: "44px"
                                  }}
                                >
                                  Upload Content
                                </button>
                                <button
                                  type="button"
                                  className="py-2"
                                  style={{
                                    width: "100%",
                                    background: "transparent",
                                    color: themePreview.text,
                                    border: `1px solid ${themePreview.gold2}`,
                                    borderRadius: 12,
                                    fontWeight: 700,
                                    minHeight: "44px"
                                  }}
                                >
                                  Show Content
                                </button>
                              </div>

                              <p className="text-center mt-3 mb-0" style={{ color: themePreview.muted }}>
                                Already uploaded: <strong style={{ color: themePreview.text }}>3</strong> / 10
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4" style={{ padding: "0.25rem 0.5rem 0.25rem 0.25rem" }}>
                  <div className="row g-2" style={{ margin: 0 }}>
                    {THEME_EDITOR_FIELDS.map(({ key, label }) => (
                      <div className="col-12" key={key}>
                        <label className="form-label d-flex justify-content-between align-items-center" style={{ marginBottom: "0.35rem" }}>
                          <span>{label}</span>
                          <span className="small text-muted">{themePreview[key] || "#7e8c73"}</span>
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            type="color"
                            value={themePreview[key] || "#7e8c73"}
                            onChange={(e) => updateThemePreview(key, e.target.value)}
                            className="form-control form-control-color"
                            style={{ width: "52px", minWidth: "52px", flex: "0 0 52px" }}
                          />
                          <input
                            type="text"
                            value={themePreview[key] || ""}
                            onChange={(e) => updateThemePreview(key, e.target.value)}
                            className="form-control"
                            placeholder="#7e8c73"
                            style={{ height: "38px" }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                background: "#ffffff",
                borderTop: "1px solid rgba(0,0,0,.08)",
                padding: "0.75rem 1rem",
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem"
              }}
            >
              <button type="button" className="btn btn-outline-secondary" onClick={resetThemePreview}>
                Reset default
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowThemeModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setEventForm(prev => ({
                    ...prev,
                    customTheme: null,
                    uploadPanelColor: themePreview["upload-panel"] || DEFAULT_CUSTOM_THEME["upload-panel"],
                    browseButtonColor: themePreview["browse-button"] || DEFAULT_CUSTOM_THEME["browse-button"],
                    uploadButtonColor: themePreview["upload-button"] || DEFAULT_CUSTOM_THEME["upload-button"]
                  }));
                  setShowThemeModal(false);
                }}
              >
                Apply to this event
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="card p-4 mb-4">
          <h3>{editingEvent ? "Edit Event" : "Add New Event"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Wedding ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={eventForm.weddingId}
                  onChange={(e) => setEventForm(prev => ({ ...prev, weddingId: e.target.value }))}
                  required
                  disabled={!!editingEvent}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Couple Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={eventForm.coupleName}
                  onChange={(e) => setEventForm(prev => ({ ...prev, coupleName: e.target.value }))}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Event Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={eventForm.eventDate}
                  onChange={(e) => setEventForm(prev => ({ ...prev, eventDate: e.target.value }))}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Event Type</label>
                <select
                  className="form-control"
                  value={eventForm.eventType}
                  onChange={(e) => setEventForm(prev => ({ ...prev, eventType: e.target.value }))}
                >
                  <option value="Wedding">Wedding</option>
                  <option value="Engagement">Engagement</option>
                  <option value="Birthday">Birthday</option>
                  <option value="Anniversary">Anniversary</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Theme</label>
                <div className="d-flex gap-2 align-items-center">
                  <select
                    className="form-control"
                    value={eventForm.theme}
                    onChange={(e) => {
                      const nextTheme = e.target.value;
                      const nextColors = THEMES[nextTheme]?.colors || {};
                      const isCustomPreset = nextTheme === "vintageWedding" || nextTheme === "customTheme2";

                      setEventForm(prev => ({
                        ...prev,
                        theme: nextTheme,
                        customTheme: null,
                        uploadPanelColor: isCustomPreset
                          ? (nextColors["upload-panel"] || prev.uploadPanelColor)
                          : prev.uploadPanelColor,
                        browseButtonColor: isCustomPreset
                          ? (nextColors["browse-button"] || prev.browseButtonColor)
                          : prev.browseButtonColor,
                        uploadButtonColor: isCustomPreset
                          ? (nextColors["upload-button"] || prev.uploadButtonColor)
                          : prev.uploadButtonColor
                      }));
                    }}
                  >
                    {Object.entries(THEMES).map(([key, theme]) => (
                      <option key={key} value={key}>{theme.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={openThemeEditor}
                  >
                    Customize
                  </button>
                </div>
                <small className="text-muted d-block mt-1">
                  Custom colors are saved per event and only affect that guest page.
                </small>
              </div>
              <div className="col-md-6">
                <label className="form-label">Background Type</label>
                <select
                  className="form-control"
                  value={eventForm.backgroundType}
                  onChange={(e) => setEventForm(prev => ({ ...prev, backgroundType: e.target.value }))}
                >
                  <option value="color">Theme Color</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
              {(eventForm.backgroundType === "image" || eventForm.backgroundType === "video") && (
                <div className="col-12">
                  <label className="form-label">{eventForm.backgroundType === "image" ? "Background Files" : "Background File"}</label>
                  <input
                    type="file"
                    className="form-control"
                    accept={eventForm.backgroundType === "image" ? "image/*" : "video/*"}
                    onChange={handleFileChange}
                    multiple={eventForm.backgroundType === "image"}
                  />

                  {eventForm.backgroundType === "image" && eventForm.backgroundImages.length > 0 && (
                    <div className="mt-3">
                      <div className="small text-muted mb-2">Background preview</div>
                      <div className="row g-2">
                        {eventForm.backgroundImages.map((image, index) => (
                          <div key={image.id || `${image.url}-${index}`} className="col-4 col-md-3 col-lg-2">
                            <div className="position-relative border rounded overflow-hidden bg-light" style={{ padding: 4 }}>
                              <img src={image.url} alt={`Background ${index + 1}`} className="img-fluid" style={{ width: "100%", height: 90, objectFit: "cover", display: "block" }} />
                              <div className="d-flex justify-content-between mt-1 gap-1">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => reorderBackgroundImage(index, Math.max(0, index - 1))} disabled={index === 0} title="Move left">←</button>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => reorderBackgroundImage(index, Math.min(eventForm.backgroundImages.length - 1, index + 1))} disabled={index === eventForm.backgroundImages.length - 1} title="Move right">→</button>
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadBackgroundFile(image, `background-${index + 1}`)} title="Download">↓</button>
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBackgroundImage(index)} title="Remove">✕</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {eventForm.backgroundType === "video" && eventForm.backgroundUrl && (
                    <div className="mt-2">
                      <video src={eventForm.backgroundUrl} controls style={{ maxWidth: "200px", maxHeight: "200px" }} />
                      <div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary mt-2"
                          onClick={() => downloadBackgroundFile({ url: eventForm.backgroundUrl, filename: eventForm.backgroundFile?.name || editingEvent?.background?.filename }, "background-video")}
                        >
                          ↓ Download Video
                        </button>
                      </div>
                    </div>
                  )}
                  {editingEvent && !eventForm.backgroundFile && editingEvent.background?.url?.toLowerCase().endsWith('.heic') && (
                    <p className="text-warning small mt-1">
                      Current background is an HEIC file; it will be converted to JPEG when you save.
                    </p>
                  )}
                </div>
              )}
              <div className="col-12">
                <div className="form-check mt-2">
                  <input
                    id="showGuestNavbar"
                    className="form-check-input"
                    type="checkbox"
                    checked={Boolean(eventForm.showGuestNavbar)}
                    onChange={(e) => setEventForm(prev => ({ ...prev, showGuestNavbar: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="showGuestNavbar">
                    Show navbar on guest upload page
                  </label>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label">Admin Emails (comma-separated)</label>
                <input
                  type="text"
                  className="form-control"
                  value={eventForm.admins.join(", ")}
                  onChange={(e) => setEventForm(prev => ({
                    ...prev,
                    admins: e.target.value.split(",").map(email => email.trim()).filter(email => email)
                  }))}
                  placeholder="admin1@example.com, admin2@example.com"
                />
              </div>
            </div>
            <div className="mt-3">
              <button type="submit" className="btn btn-primary me-2">
                {editingEvent ? "Update Event" : "Create Event"}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="super-admin-card">
        <div className="super-admin-card-header">
          <h3 className="mb-0">All Events ({events.length})</h3>
        </div>
        <div className="super-admin-card-body">
          {loading ? (
            <p>Loading events...</p>
          ) : events.length === 0 ? (
            <div className="super-admin-empty-card">
              <div className="super-admin-empty-icon">📅</div>
              <h3 className="super-admin-empty-title">No events yet</h3>
              <p className="super-admin-empty-text">Create your first event to start collecting guest uploads.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Couple</th>
                    <th>Event Date</th>
                    <th>Type</th>
                    <th>Guest Nav</th>
                    <th>Theme</th>
                    <th>Background</th>
                    <th>Admins</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(event => (
                    <tr key={event.id}>
                      <td>{event.coupleName}</td>
                      <td>{formatDate(event.eventDate)}</td>
                      <td>{event.eventType}</td>
                      <td>{event.showGuestNavbar === false ? "Hidden" : "Visible"}</td>
                      <td>{getThemeLabel(event)}</td>
                      <td>{event.background?.type || "color"}</td>
                      <td>{event.admins?.length || 0} admins</td>
                      <td>
                        <button
                          onClick={() => handleEdit(event)}
                          className="btn btn-sm btn-outline-primary me-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(event)}
                          className="btn btn-sm btn-outline-danger"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Events Section */}
      <div className="super-admin-card super-admin-card-upcoming">
        <div className="super-admin-card-header">
          <h3 className="mb-0">Upcoming Events ({upcomingEvents.length})</h3>
        </div>
        <div className="super-admin-card-body">
          {upcomingLoading ? (
            <p>Loading upcoming events...</p>
          ) : upcomingEvents.length === 0 ? (
            <div className="super-admin-empty-card super-admin-empty-card-small">
              <div className="super-admin-empty-icon">✨</div>
              <h3 className="super-admin-empty-title">No upcoming events</h3>
              <p className="super-admin-empty-text">New event plans will appear here once they are scheduled.</p>
            </div>
          ) : (
            <div className="row g-4">
              {upcomingEvents.map(event => {
                return (
                  <div key={event.id} className="col-lg-6 col-xl-4">
                    <div className="card h-100 shadow-sm">
                      {/* Event background preview */}
                      <div
                        className="card-img-top"
                        style={{
                          height: "200px",
                          background: event.background?.type === "image" && event.background.url
                            ? `url(${event.background.url})`
                            : "#f8f9fa",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          borderBottom: "1px solid #dee2e6"
                        }}
                      >
                        {event.background?.type === "video" && event.background.url && (
                          <video
                            src={event.background.url}
                            muted
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover"
                            }}
                          />
                        )}
                      </div>

                      <div className="card-body d-flex flex-column">
                        <h5 className="card-title">
                          {event.coupleName}
                        </h5>
                        <p className="card-text mb-2">
                          {event.eventType} - {formatDate(event.eventDate)}
                        </p>
                        <p className="card-text mb-3">
                          Theme: {getThemeLabel(event)}
                        </p>

                        <div className="mt-auto">
                          <a
                            href={`/event/${event.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary w-100"
                          >
                            View Event Page
                          </a>
                          <a
                            href={`/admin/${event.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-secondary w-100 mt-2"
                          >
                            Admin Access
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}